<script setup>
import Layout from "../../layouts/main.vue";
import mushroom from "cem-primary-api";
import { useRoute, useRouter } from "vue-router";
import { ref, reactive, onMounted, computed } from "vue";
import { FormInstance } from "element-plus";
import Swal from "sweetalert2";
import toastr from "toastr";
import MethodService from "../../service/MethodService";
import IconUploadSelector from "@/components/IconUploadSelector.vue";
import { Filter } from "mushroomjs";
import noneIcon from "@/assets/icon/none-icon-svg.svg";
import { useI18n } from "vue-i18n";
const { t } = useI18n();
import Data from "./dataServiceGroup";
const ruleFormRef = ref(FormInstance);
const serviceGroupConfig = ref({});
const dataForm = reactive({
  value: MethodService.copyObject(Data.dataForm),
});
const dataTables = reactive({ value: [] });
let loading = ref(false);
const searchServiceInGroup = ref("");
const tableRules = reactive({
  page: 1,
  limit: 20,
  total: 0,
  offset: 0,
  lengthMenu: [10, 20, 50, 100],
});
const pageSizeDefault = ref(20);
const showServicePopup = ref(false);
const allServices = ref([]);
const selectedServiceIds = ref([]);
const searchAllService = ref("");
const popupTableRules = reactive({
  page: 1,
  limit: 20,
  total: 0,
  offset: 0,
  lengthMenu: [10, 20, 50, 100],
});
const popupPageSizeDefault = ref(20);
let popupLoading = ref(false);

const displayedData = computed(() => dataTables.value);

const fetchListServicesByIdAsync = async () => {
  if (
    !dataForm.value.list_service ||
    dataForm.value.list_service.length === 0
  ) {
    dataTables.value = [];
    tableRules.total = 0;
    return;
  }
  loading.value = true;
  try {
    let filter = Filter.in("id", dataForm.value.list_service || []);
    if (searchServiceInGroup.value) {
      filter = filter.and(
        Filter.regex("name", searchServiceInGroup.value, "i"),
      );
    }
    const res = await mushroom.service_health.listAsync({
      filters: filter.build(),
      limit: tableRules.limit,
      offset: tableRules.offset,
    });
    dataTables.value = res.result || [];
    tableRules.total = res.meta?.total || 0;
  } catch (e) {
    console.error("Error fetching services:", e);
  } finally {
    loading.value = false;
  }
};

const fn_tableSizeChange = async (limit) => {
  tableRules.limit = limit;
  pageSizeDefault.value = limit;
  tableRules.page = 1;
  tableRules.offset = 0;
  await fetchListServicesByIdAsync();
};

const fn_tableCurentChange = async (page) => {
  tableRules.page = page;
  tableRules.offset = (page - 1) * tableRules.limit;
  await fetchListServicesByIdAsync();
};

const categories = ref([]);
const validForm = reactive({ value: Data.validForm });
const router = useRoute();
const routerPush = useRouter();
const setIcon = (id) => {
  dataForm.value.icon_id = id;
};
const fetchCategoriesAsync = async () => {
  try {
    const res = await mushroom.service_health_category.views.getListAsync({
      _limit: 1000,
    });
    categories.value = res.result || [];
  } catch (e) {
    console.error("Error fetching categories:", e);
  }
};

const fetchServiceGroupAsync = async () => {
  try {
    const { result } = await mushroom.service_health_group.findByIdAsync({
      id: router.params.id,
    });
    if (result) {
      dataForm.value = result;
      await fetchListServicesByIdAsync();
    }
  } catch (e) {
    MethodService.showError(e.code);
  }
};

const fetchServiceGroupConfig = async () => {
  try {
    const { result } = await mushroom.system_config.listAsync({
      filters: Filter.eq("key", "service_group_default_config").build(),
    });
    serviceGroupConfig.value = result[0]?.value || {};
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};

const addServiceGroup = (formEl) => {
  if (!formEl) return;
  formEl.validate(async (valid) => {
    if (valid) {
      try {
        const idService = await mushroom.service_health_group.createAsync(
          Object.assign(dataForm.value, JSON.parse(serviceGroupConfig.value)),
        );
        if (idService) toastr.success(t("toastr_add_success"));
        routerPush.go(-1);
      } catch (error) {
        const errorMap = {
          1002: "toastr_service_group_duplicate",
          1003: "toastr_service_group_in_use",
          1004: "toastr_group_max_for_menu",
        };

        const subMsg = error?.code === 400 ? errorMap[error.subCode] : null;

        if (subMsg) {
          toastr.error(t(subMsg));
        } else {
          toastr.error(t("toastr_error"));
        }
      }
    } else return false;
  });
};

const updateServiceGroup = (formEl) => {
  if (!formEl) return;
  formEl.validate(async (valid) => {
    if (valid) {
      const serviceData = {
        id: router.params.id,
        name: dataForm.value.name,
        icon_id: dataForm.value.icon_id,
        list_service: dataForm.value.list_service,
        menu: dataForm.value.menu,
        description: dataForm.value.description,
      };
      try {
        const idService =
          await mushroom.service_health_group.partialUpdateAsync(serviceData);
        if (idService.result === router.params.id)
          toastr.success(t("toastr_update_success"));
        routerPush.go(-1);
      } catch (error) {
        const errorMap = {
          1002: "toastr_service_group_duplicate",
          1003: "toastr_service_group_in_use",
          1004: "toastr_group_max_for_menu",
        };

        const subMsg = error?.code === 400 ? errorMap[error.subCode] : null;

        if (subMsg) {
          toastr.error(t(subMsg));
        } else {
          toastr.error(t("toastr_error"));
        }
      }
    } else return false;
  });
};

const renderIcon = (iconId, icon) => {
  if (iconId) {
    return mushroom.$file.linkBuilder.thumb.id(iconId).build();
  }
  if (icon) {
    return icon;
  }
  return noneIcon;
};

const getGroupName = (id) => {
  const cat = categories.value.find((c) => c.group_id == id);
  return cat ? cat.group_name : id;
};

const addServiceToGroup = async () => {
  showServicePopup.value = true;
  selectedServiceIds.value = [];
  popupTableRules.page = 1;
  popupTableRules.offset = 0;
  searchAllService.value = "";
  await fetchAllServicesAsync();
};

const fetchAllServicesAsync = async () => {
  popupLoading.value = true;
  try {
    let filter = Filter.nin("id", dataForm.value.list_service || []);
    if (searchAllService.value) {
      filter = filter.and(Filter.regex("name", searchAllService.value, "i"));
    }
    console.log("filter111: %o", filter.build());
    const res = await mushroom.service_health.listAsync({
      filters: filter.build(),
      limit: popupTableRules.limit,
      offset: popupTableRules.offset,
    });
    allServices.value = res.result || [];
    console.log("1111", allServices.value);
    popupTableRules.total = res.meta?.total || 0;
  } catch (e) {
    console.error("Error fetching all services:", e);
  } finally {
    popupLoading.value = false;
  }
};

const toggleServiceInPopup = (id) => {
  const index = selectedServiceIds.value.indexOf(id);
  if (index > -1) {
    selectedServiceIds.value.splice(index, 1);
  } else {
    selectedServiceIds.value.push(id);
  }
};

const confirmAddServices = async () => {
  if (selectedServiceIds.value.length === 0) {
    toastr.warning(t("toastr_please_select_service"));
    return;
  }
  if (!dataForm.value.list_service) dataForm.value.list_service = [];
  dataForm.value.list_service.push(...selectedServiceIds.value);
  showServicePopup.value = false;
  await fetchListServicesByIdAsync();
};

const fn_popupTableSizeChange = async (limit) => {
  popupTableRules.limit = limit;
  popupPageSizeDefault.value = limit;
  popupTableRules.page = 1;
  popupTableRules.offset = 0;
  await fetchAllServicesAsync();
};

const fn_popupTableCurentChange = async (page) => {
  popupTableRules.page = page;
  popupTableRules.offset = (page - 1) * popupTableRules.limit;
  await fetchAllServicesAsync();
};

const handleDelete = (row) => {
  Swal.fire({
    title: t("t_warning"),
    text: t("t_service_remove_from_group"),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#f46a6a",
    confirmButtonText: t("swal_accept"),
    cancelButtonText: t("swal_cancel"),
    allowOutsideClick: false,
  }).then(async (result) => {
    if (result.isConfirmed) {
      if (dataForm.value.list_service) {
        dataForm.value.list_service = dataForm.value.list_service.filter(
          (id) => id !== row.id,
        );
      }
      if (dataTables.value) {
        dataTables.value = dataTables.value.filter(
          (item) => item.id !== row.id,
        );
      }
    }
  });
};

onMounted(async () => {
  await fetchServiceGroupConfig();
  await fetchCategoriesAsync();
  if (router.name === "CapNhatServiceHealthGroup") {
    await fetchServiceGroupAsync();
  }
});
</script>

<template>
  <Layout>
    <div class="card">
      <div class="card-header">
        <h3 v-if="router.name === 'ThemMoiServiceHealthGroup'">
          {{ $t("t_add_service_health_group") }}
        </h3>
        <h3 v-else>{{ $t("t_update_service_health_group") }}</h3>
      </div>
      <div class="card-body">
        <div class="main-body">
          <el-form
            ref="ruleFormRef"
            :model="dataForm.value"
            :rules="validForm.value"
            status-icon
            label-width="150px"
            label-position="left"
            class="form-user"
          >
            <el-form-item :label="$t('t_group_name')" prop="name">
              <el-input
                type="text"
                autocomplete="off"
                v-model="dataForm.value.name"
                :placeholder="$t('t_place_group_name')"
              />
            </el-form-item>

            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item :label="$t('t_icon')">
                  <icon-upload-selector
                    v-model="dataForm.value.icon_id"
                    @setIcon="setIcon"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12" style="text-align: right">
                <el-form-item>
                  <el-switch
                    v-model="dataForm.value.menu"
                    :active-text="$t('t_menu')"
                  />
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item
              :label="$t('t_service_group_description')"
              prop="description"
            >
              <el-input
                type="textarea"
                :rows="2"
                autocomplete="off"
                v-model="dataForm.value.description"
                :placeholder="$t('t_place_service_group_description')"
              />
            </el-form-item>

            <div class="text-center">
              <button
                type="button"
                class="btn btn-soft-secondary btn-border mx-3"
                @click="routerPush.go(-1)"
              >
                {{ $t("t-back") }}
              </button>
              <button
                type="button"
                class="btn btn-secondary btn-border"
                @click="addServiceGroup(ruleFormRef)"
                v-if="router.name === 'ThemMoiServiceHealthGroup'"
              >
                {{ $t("t-add") }}
              </button>
              <button
                type="button"
                class="btn btn-secondary btn-border"
                @click="updateServiceGroup(ruleFormRef)"
                v-else
              >
                {{ $t("t-update") }}
              </button>
            </div>
          </el-form>
        </div>
      </div>
    </div>

    <div class="card" v-if="router.name === 'CapNhatServiceHealthGroup'">
      <div class="card-header">
        <div style="display: flex; align-items: center; gap: 10px">
          <h3>{{ $t("t_list_service_in_group") }}</h3>
          <span
            class="badge bg-success"
            v-if="tableRules.total > 0"
            style="font-size: 12px; line-height: 1.5"
          >
            {{ tableRules.total }} {{ $t("t_service_for_length") }}
          </span>
          <span
            v-else
            class="badge bg-warning"
            style="font-size: 12px; line-height: 1.5"
            >{{ $t("t_no_data_service") }}</span
          >
        </div>
      </div>
      <div class="card-body">
        <div class="mb-3 d-flex justify-content-between align-items-center">
          <div style="display: flex; align-items: center; gap: 10px">
            <el-input
              v-model="searchServiceInGroup"
              :placeholder="$t('t_search_service_in_group')"
              clearable
              style="width: 250px"
              size="large"
              @keyup.enter="fetchListServicesByIdAsync"
            >
              <template #prefix>
                <i class="ri-search-line"></i>
              </template>
            </el-input>
            <button
              class="btn btn-secondary btn-border"
              @click="fetchListServicesByIdAsync"
            >
              {{ $t("t-search") }}
            </button>
          </div>
          <button
            class="btn btn-secondary btn-border mb-3 d-flex justify-content-between align-items-center"
            @click="addServiceToGroup()"
          >
            <i class="ri-add-line"></i>
            {{ $t("t_add_service") }}
          </button>
        </div>
        <el-table
          border
          size="small"
          max-height="400"
          stripe
          :data="displayedData"
          style="width: 100%"
          v-loading="loading"
          :empty-text="$t('t-no-data')"
        >
          <el-table-column :label="$t('t-stt')" width="60" align="center">
            <template #default="scope">
              {{ tableRules.offset + scope.$index + 1 }}
            </template>
          </el-table-column>
          <el-table-column
            prop="name"
            :label="$t('t_service_name')"
            align="center"
            min-width="120"
            sortable="custom"
          >
            <template #default="scope">
              <div class="text-start">
                <img
                  :src="renderIcon(scope.row.icon_id, scope.row.icon)"
                  alt="icon"
                  class="me-2"
                  style="height: 25px; width: 25px; object-fit: contain"
                />{{ scope.row.name }}
              </div>
            </template>
          </el-table-column>
          <el-table-column
            prop="home_page"
            :label="$t('t_home_page')"
            min-width="120"
            align="center"
            show-overflow-tooltip
          >
            <template #default="scope">
              <div class="col-tooltip">
                {{ scope.row.home_page }}
              </div>
            </template>
          </el-table-column>
          <el-table-column
            prop="main_group_id"
            align="center"
            :label="$t('t_main_group')"
            min-width="100"
            show-overflow-tooltip
          >
            <template #default="scope">
              <div class="col-tooltip">
                {{ getGroupName(scope.row.main_group_id) }}
              </div>
            </template>
          </el-table-column>
          <el-table-column
            prop="description"
            :label="$t('t_description')"
            align="center"
            min-width="300"
            show-overflow-tooltip
          >
            <template #default="scope">
              <div class="col-tooltip">{{ scope.row.description }}</div>
            </template>
          </el-table-column>
          <el-table-column
            prop="create_by"
            :label="$t('t_create_by')"
            align="center"
            width="100"
          >
            <template #default="scope">
              <span
                v-if="scope.row.create_by === 'System'"
                class="badge badge-soft-danger"
                >System</span
              >
              <span
                v-else-if="scope.row.create_by === 'Manual'"
                class="badge badge-soft-secondary"
                >Manual</span
              >
              <span v-else class="badge badge-soft-info">{{
                scope.row.create_by
              }}</span>
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('t-action')"
            width="80"
            align="center"
            fixed="right"
          >
            <template #default="scope">
              <div class="btn-group-thao-tac">
                <el-tooltip
                  :content="$t('action_delete')"
                  effect="light"
                  placement="top"
                >
                  <button
                    class="btn btn-soft-danger waves-effect waves-light btn-sm"
                    @click="handleDelete(scope.row)"
                  >
                    <i class="ri-delete-bin-6-line"></i>
                  </button>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>
        </el-table>
        <div class="mt-3" style="float: right">
          <el-pagination
            small
            v-model:currentPage="tableRules.page"
            v-model:pageSize="pageSizeDefault"
            :page-sizes="tableRules.lengthMenu"
            background
            layout="total, sizes, prev, pager, next, jumper"
            :total="tableRules.total"
            @size-change="fn_tableSizeChange"
            @current-change="fn_tableCurentChange"
          />
        </div>
      </div>
    </div>

    <el-dialog
      v-model="showServicePopup"
      width="80%"
      destroy-on-close
      align-center
    >
      <template #header>
        <div class="d-flex align-items-center">
          <h5 class="mb-0">{{ $t("t_add_service") }}</h5>
        </div>
      </template>
      <div class="mb-3 d-flex align-items-center gap-2">
        <el-input
          v-model="searchAllService"
          :placeholder="$t('t_search_service_in_group')"
          clearable
          style="width: 250px"
          size="large"
          @keyup.enter="fetchAllServicesAsync"
        >
          <template #prefix>
            <i class="ri-search-line"></i>
          </template>
        </el-input>
        <button
          class="btn btn-secondary btn-border"
          @click="fetchAllServicesAsync"
        >
          {{ $t("t-search") }}
        </button>
      </div>

      <el-table
        size="small"
        max-height="450"
        stripe
        border
        :data="allServices"
        v-loading="popupLoading"
      >
        <el-table-column :label="$t('t-stt')" width="60" align="center">
          <template #default="scope">
            {{ popupTableRules.offset + scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column
          prop="name"
          :label="$t('t_service_name')"
          min-width="150"
          align="center"
        >
          <template #default="scope">
            <div class="d-flex align-items-center">
              <img
                :src="renderIcon(scope.row.icon_id, scope.row.icon)"
                alt="icon"
                class="me-2"
                style="height: 25px; width: 25px; object-fit: contain"
              />
              {{ scope.row.name }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="home_page"
          :label="$t('t_home_page')"
          min-width="120"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div class="col-tooltip">
              {{ scope.row.home_page }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="main_group_id"
          :label="$t('t_main_group')"
          min-width="100"
          align="center"
          show-overflow-tooltip
        >
          <template #default="scope">
            <div class="col-tooltip">
              {{ getGroupName(scope.row.main_group_id) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="description"
          :label="$t('t_description')"
          min-width="200"
          show-overflow-tooltip
        />
        <el-table-column
          prop="create_by"
          :label="$t('t_create_by')"
          align="center"
          width="100"
        >
          <template #default="scope">
            <span
              v-if="scope.row.create_by === 'System'"
              class="badge badge-soft-danger"
              >System</span
            >
            <span
              v-else-if="scope.row.create_by === 'Manual'"
              class="badge badge-soft-secondary"
              >Manual</span
            >
            <span v-else class="badge badge-soft-info">{{
              scope.row.create_by
            }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('t-action')" width="90" align="center">
          <template #default="scope">
            <div class="btn-group-thao-tac">
              <el-tooltip
                :content="
                  selectedServiceIds.includes(scope.row.id)
                    ? $t('t_added')
                    : $t('t_add')
                "
                placement="top"
                effect="light"
              >
                <button
                  :class="
                    selectedServiceIds.includes(scope.row.id)
                      ? 'btn btn-soft-success waves-effect waves-light btn-sm'
                      : 'btn btn-soft-primary waves-effect waves-light btn-sm'
                  "
                  @click="toggleServiceInPopup(scope.row.id)"
                >
                  <i
                    :class="
                      selectedServiceIds.includes(scope.row.id)
                        ? 'ri-check-line'
                        : 'ri-add-line'
                    "
                  ></i>
                </button>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="mt-3 d-flex justify-content-between align-items-center">
        <el-pagination
          small
          v-model:currentPage="popupTableRules.page"
          v-model:pageSize="popupPageSizeDefault"
          :page-sizes="popupTableRules.lengthMenu"
          background
          layout="total, sizes, prev, pager, next"
          :total="popupTableRules.total"
          @size-change="fn_popupTableSizeChange"
          @current-change="fn_popupTableCurentChange"
        />
        <div class="text-end">
          <button
            class="btn btn-secondary btn-border"
            @click="confirmAddServices"
          >
            {{ $t("t_add_service_to_group") }}
          </button>
        </div>
      </div>
    </el-dialog>
  </Layout>
</template>
